import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { MessageInfo } from '@extension/context/page/services/message-info.types';
import { ContentMessageServiceActions } from '@extension/context/content/services/contentMessageServiceActions';
import { isExtensionContext } from '@common/services/detect-context';

/**
 * State interface for message info
 */
export interface MessageInfoState {
    /**
     * Map of message info by message ID (waMessageKey)
     */
    messageInfoMap: Record<string, MessageInfo>;
    
    /**
     * Loading states by message ID
     */
    loadingStates: Record<string, boolean>;
    
    /**
     * Error states by message ID
     */
    errorStates: Record<string, string>;
}

const initialState: MessageInfoState = {
    messageInfoMap: {},
    loadingStates: {},
    errorStates: {},
};

/**
 * Async thunk to fetch message info
 * Tool-based architecture: Uses isExtensionContext() for dual-mode execution
 * - Extension Mode: ContentMessageServiceActions.getMessageInfo()
 * - Server Mode: RTK Query mutation via backend API → MQTT → Engine
 */
export const waFetchMessageInfoTool = createAsyncThunk(
    'messageInfo/waFetchMessageInfoTool',
    async ({ waMessageKey, getMessageInfoMutation }: { 
        waMessageKey: string; 
        getMessageInfoMutation?: any; 
    }, { rejectWithValue }) => {
        try {
            let response: MessageInfo | null;
            
            if (isExtensionContext()) {
                // Direct call in extension context
                response = await ContentMessageServiceActions.getMessageInfo(waMessageKey);
            } else {
                // Use RTK Query mutation in website context
                if (!getMessageInfoMutation) {
                    throw new Error('getMessageInfoMutation is required in website context');
                }
                response = await getMessageInfoMutation({ waMessageKey }).unwrap();
            }
            
            return { waMessageKey, messageInfo: response };
        } catch (error) {
            console.error('[waFetchMessageInfoTool] Error:', error);
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch message info');
        }
    }
);

/**
 * Message info slice
 */
const messageInfoSlice = createSlice({
    name: 'messageInfo',
    initialState,
    reducers: {
        /**
         * Clear message info for a specific message
         */
        clearMessageInfo: (state, action: PayloadAction<string>) => {
            const waMessageKey = action.payload;
            delete state.messageInfoMap[waMessageKey];
            delete state.loadingStates[waMessageKey];
            delete state.errorStates[waMessageKey];
        },
        
        /**
         * Clear all message info
         */
        clearAllMessageInfo: (state) => {
            state.messageInfoMap = {};
            state.loadingStates = {};
            state.errorStates = {};
        },
        
        /**
         * Set message info directly (for cases where we have the data already)
         */
        setMessageInfo: (state, action: PayloadAction<{ waMessageKey: string; messageInfo: MessageInfo }>) => {
            const { waMessageKey, messageInfo } = action.payload;
            state.messageInfoMap[waMessageKey] = messageInfo;
            delete state.loadingStates[waMessageKey];
            delete state.errorStates[waMessageKey];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(waFetchMessageInfoTool.pending, (state, action) => {
                const waMessageKey = action.meta.arg.waMessageKey;
                state.loadingStates[waMessageKey] = true;
                delete state.errorStates[waMessageKey];
            })
            .addCase(waFetchMessageInfoTool.fulfilled, (state, action) => {
                const { waMessageKey, messageInfo } = action.payload;
                state.loadingStates[waMessageKey] = false;
                if (messageInfo) {
                    state.messageInfoMap[waMessageKey] = messageInfo;
                }
                delete state.errorStates[waMessageKey];
            })
            .addCase(waFetchMessageInfoTool.rejected, (state, action) => {
                const waMessageKey = action.meta.arg.waMessageKey;
                state.loadingStates[waMessageKey] = false;
                state.errorStates[waMessageKey] = action.payload as string || 'Failed to fetch message info';
            });
    },
});

export const {
    clearMessageInfo,
    clearAllMessageInfo,
    setMessageInfo,
} = messageInfoSlice.actions;

export default messageInfoSlice.reducer;
